<script setup lang="ts">
/**
 * Setting and editing KPIs (#895): one row per KPI — what is measured and its unit, the target and which
 * way is good, the AMBER band, the cadence and the source — plus which one is the key KPI.
 *
 * It refuses nothing on its own. The one validator lives in `@zerohuman/db` and runs on the API, so the
 * portal, the API and the MCP server refuse the same input rather than the portal keeping a second copy
 * of the rules that could drift; what the API refuses comes back as the error the caller shows. Leaving
 * the target empty is not a refusal: a KPI with no target yet is accepted, and reads as having none.
 */
import { NO_KEY_KPI } from '@zerohuman/shared'
import { computed, reactive, useId } from 'vue'
import SearchSelect from '@/components/SearchSelect.vue'
import { useKpiSources } from '@/composables/useKpiSources'
import {
    emptyKpi,
    KPI_CADENCE_PERIODS,
    KPI_DIRECTION_OPTIONS,
    KPI_UNIT_SUGGESTIONS,
    type Kpi,
    type KpiBandType,
    type KpiCadencePeriod,
    type KpiDirection,
    kpiCadencePeriod,
    kpiCadencePeriodMinutes,
    kpiDirectionText,
} from '@/lib/kpi'

const props = defineProps<{
    modelValue: Kpi[]
    /** Team charters have at most one key KPI — the one the company sees first; the company's own list has none. */
    keyKpi?: boolean
}>()
const emit = defineEmits<{ 'update:modelValue': [Kpi[]] }>()

/** One list of suggested units for every row's unit field; the field takes any other unit as typed. */
const unitSuggestionsId = `${useId()}-units`

/** What a KPI's source is picked from, read once for every row: people for a person, tasks for a task. */
const sources = useKpiSources()

function sourcePlaceholder(kpi: Kpi): string {
    if (kpi.sourceKind === 'person') return 'Search people'
    return kpi.sourceKind === 'task' ? 'Search tasks' : 'Choose a source kind first'
}

/**
 * A team's KPIs with none marked key are accepted as they are (CTO decision 7 on #895): nothing picks one for the
 * team. The editor says so in the words HQ's board and the teams list use, rather than leaving it to be noticed.
 */
const noKeyKpi = computed(() => props.keyKpi && props.modelValue.length > 0 && !props.modelValue.some(kpi => kpi.isKey))

function write(index: number, patch: Partial<Kpi>): void {
    emit(
        'update:modelValue',
        props.modelValue.map((kpi, at) => (at === index ? { ...kpi, ...patch } : kpi)),
    )
}

/**
 * What a number field holds: `null` when it is empty — "no target", not zero, since `Number('')` is 0 and would
 * invent a target nobody set — or the number typed. `undefined` for text that is not a number yet, such as `-` or
 * `.` on the way to `-5` or `.5`: that is a number being typed, not a cleared field, so nothing is written.
 */
function numberOrNull(value: string): number | null | undefined {
    const trimmed = value.trim()
    if (trimmed === '') return null
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : undefined
}

type NumberField = 'target' | 'band' | 'cadence'

/**
 * What was last typed in each number field. A field is redrawn from the number it holds, which would turn "99."
 * on the way to "99.5" back into "99"; so while the text typed still says the stored number, the text is shown.
 */
const typed = reactive(new Map<string, string>())

function keepTyped(index: number, field: NumberField, value: string): void {
    const kpi = props.modelValue[index]
    if (kpi) typed.set(`${kpi.id}:${field}`, value)
}

function fieldText(kpi: Kpi, field: NumberField, stored: number | null): string {
    const text = typed.get(`${kpi.id}:${field}`)
    if (text !== undefined) {
        const parsed = numberOrNull(text)
        if (parsed === undefined || parsed === stored) return text
    }
    return stored === null ? '' : String(stored)
}

function setTarget(index: number, value: string): void {
    keepTyped(index, 'target', value)
    const target = numberOrNull(value)
    if (target === undefined) return
    // Clearing the target clears what only a target can be read against, so no half-set target is sent.
    write(
        index,
        target === null
            ? {
                  targetValue: null,
                  targetDirection: null,
                  amberBand: null,
                  amberBandType: null,
                  cadenceMinutes: null,
              }
            : { targetValue: target },
    )
}

function setBand(index: number, value: string): void {
    keepTyped(index, 'band', value)
    const band = numberOrNull(value)
    if (band === undefined) return
    write(
        index,
        band === null
            ? { amberBand: null, amberBandType: null }
            : {
                  amberBand: band,
                  amberBandType: props.modelValue[index]?.amberBandType ?? 'absolute',
              },
    )
}

/**
 * The period each row's cadence is typed in, once someone picks one. Until then it is read off the stored minutes,
 * so a cadence set elsewhere shows in the longest period it is a whole number of.
 */
const chosenPeriods = reactive(new Map<string, KpiCadencePeriod>())

function cadencePeriod(kpi: Kpi): KpiCadencePeriod {
    return chosenPeriods.get(kpi.id) ?? kpiCadencePeriod(kpi.cadenceMinutes)
}

/** How many of its period a row's cadence is: what the number field shows. `null` for a KPI with no cadence. */
function cadenceCount(kpi: Kpi): number | null {
    return kpi.cadenceMinutes === null ? null : kpi.cadenceMinutes / kpiCadencePeriodMinutes(cadencePeriod(kpi))
}

/** The API holds a cadence as whole minutes, so a count of a period is sent as the nearest whole minute. */
function cadenceMinutes(count: number, period: KpiCadencePeriod): number {
    return Math.round(count * kpiCadencePeriodMinutes(period))
}

function setCadence(index: number, value: string): void {
    keepTyped(index, 'cadence', value)
    const count = numberOrNull(value)
    const kpi = props.modelValue[index]
    if (count === undefined || !kpi) return
    write(index, { cadenceMinutes: count === null ? null : cadenceMinutes(count, cadencePeriod(kpi)) })
}

/** Changing the period keeps the number as typed: "2 days" becomes "2 weeks", not "0.29 weeks". */
function setCadencePeriod(index: number, value: string): void {
    const kpi = props.modelValue[index]
    if (!kpi) return
    const count = cadenceCount(kpi)
    const period = value as KpiCadencePeriod
    chosenPeriods.set(kpi.id, period)
    if (count !== null) write(index, { cadenceMinutes: cadenceMinutes(count, period) })
}

function setDirection(index: number, value: string): void {
    write(index, {
        targetDirection: value === '' ? null : (value as KpiDirection),
    })
}

function setBandType(index: number, value: string): void {
    write(index, { amberBandType: value === '' ? null : (value as KpiBandType) })
}

/** A source is a person or a task, so one picked under the other kind means nothing: changing the kind clears it. */
function setSourceKind(index: number, value: string): void {
    const kind = value === '' ? null : (value as Kpi['sourceKind'])
    if (kind === (props.modelValue[index]?.sourceKind ?? null)) return
    write(index, { sourceKind: kind, sourceRef: null })
}

/** Marking one key unmarks the rest here, so the list the API sees never holds two keys to refuse. */
function setKey(index: number): void {
    emit(
        'update:modelValue',
        props.modelValue.map((kpi, at) => ({ ...kpi, isKey: at === index })),
    )
}

function add(): void {
    emit('update:modelValue', [...props.modelValue, emptyKpi(`kpi-${Date.now()}-${props.modelValue.length + 1}`)])
}

function remove(index: number): void {
    emit(
        'update:modelValue',
        props.modelValue.filter((_, at) => at !== index),
    )
}
</script>

<template>
  <div data-testid="kpi-list-editor" class="space-y-3">
    <datalist :id="unitSuggestionsId" data-testid="kpi-unit-suggestions">
      <option v-for="unit in KPI_UNIT_SUGGESTIONS" :key="unit" :value="unit" />
    </datalist>
    <p
      v-if="noKeyKpi"
      data-testid="kpi-no-key"
      class="text-muted-foreground text-sm"
    >
      {{ NO_KEY_KPI }} yet. Mark one as the key KPI to put its light on HQ.
    </p>
    <div
      v-for="(kpi, index) in modelValue"
      :key="kpi.id"
      :data-testid="`kpi-row-${index}`"
      class="border-border space-y-2 rounded-md border p-3"
    >
      <div class="grid gap-2 sm:grid-cols-2">
        <label class="block text-sm">
          <span class="text-muted-foreground text-xs">What is measured</span>
          <input
            :value="kpi.name"
            :data-testid="`kpi-name-${index}`"
            class="border-input bg-background mt-1 w-full rounded-md border px-2 py-1 text-sm"
            @input="write(index, { name: ($event.target as HTMLInputElement).value })"
          />
        </label>
        <label class="block text-sm">
          <span class="text-muted-foreground text-xs">Unit</span>
          <input
            :value="kpi.unit"
            :data-testid="`kpi-unit-${index}`"
            :list="unitSuggestionsId"
            placeholder="hours, %, signups/week"
            class="border-input bg-background mt-1 w-full rounded-md border px-2 py-1 text-sm"
            @input="write(index, { unit: ($event.target as HTMLInputElement).value })"
          />
        </label>
        <label class="block text-sm">
          <span class="text-muted-foreground text-xs">Which way is good</span>
          <select
            :value="kpi.targetDirection ?? ''"
            :data-testid="`kpi-direction-${index}`"
            class="border-input bg-background mt-1 w-full rounded-md border px-2 py-1 text-sm"
            @change="setDirection(index, ($event.target as HTMLSelectElement).value)"
          >
            <option value="">—</option>
            <option
              v-for="direction in KPI_DIRECTION_OPTIONS"
              :key="direction.value"
              :value="direction.value"
            >
              {{ direction.label }} ({{ kpiDirectionText(direction.value) }} the target)
            </option>
          </select>
        </label>
        <label class="block text-sm">
          <span class="text-muted-foreground text-xs"
            >Target (leave empty for no target yet)</span
          >
          <input
            :value="fieldText(kpi, 'target', kpi.targetValue)"
            :data-testid="`kpi-target-${index}`"
            inputmode="decimal"
            class="border-input bg-background mt-1 w-full rounded-md border px-2 py-1 text-sm"
            @input="setTarget(index, ($event.target as HTMLInputElement).value)"
          />
        </label>
        <label class="block text-sm">
          <span class="text-muted-foreground text-xs">AMBER band</span>
          <input
            :value="fieldText(kpi, 'band', kpi.amberBand)"
            :data-testid="`kpi-band-${index}`"
            inputmode="decimal"
            class="border-input bg-background mt-1 w-full rounded-md border px-2 py-1 text-sm"
            @input="setBand(index, ($event.target as HTMLInputElement).value)"
          />
        </label>
        <label class="block text-sm">
          <span class="text-muted-foreground text-xs">Band is</span>
          <select
            :value="kpi.amberBandType ?? ''"
            :data-testid="`kpi-band-type-${index}`"
            class="border-input bg-background mt-1 w-full rounded-md border px-2 py-1 text-sm"
            @change="setBandType(index, ($event.target as HTMLSelectElement).value)"
          >
            <option value="">—</option>
            <option value="absolute">an absolute distance</option>
            <option value="percentage">a percentage of the target</option>
          </select>
        </label>
        <div class="block text-sm">
          <span class="text-muted-foreground text-xs">Read every</span>
          <span class="mt-1 flex gap-2">
            <input
              :value="fieldText(kpi, 'cadence', cadenceCount(kpi))"
              :data-testid="`kpi-cadence-${index}`"
              aria-label="Read every: how many"
              inputmode="numeric"
              class="border-input bg-background w-full rounded-md border px-2 py-1 text-sm"
              @input="setCadence(index, ($event.target as HTMLInputElement).value)"
            />
            <select
              :value="cadencePeriod(kpi)"
              :data-testid="`kpi-cadence-period-${index}`"
              aria-label="Read every: minutes, hours, days or weeks"
              class="border-input bg-background w-full rounded-md border px-2 py-1 text-sm"
              @change="setCadencePeriod(index, ($event.target as HTMLSelectElement).value)"
            >
              <option
                v-for="period in KPI_CADENCE_PERIODS"
                :key="period.value"
                :value="period.value"
              >
                {{ period.label }}
              </option>
            </select>
          </span>
        </div>
        <label class="block text-sm">
          <span class="text-muted-foreground text-xs">Source kind</span>
          <select
            :value="kpi.sourceKind ?? ''"
            :data-testid="`kpi-source-kind-${index}`"
            class="border-input bg-background mt-1 w-full rounded-md border px-2 py-1 text-sm"
            @change="setSourceKind(index, ($event.target as HTMLSelectElement).value)"
          >
            <option value="">—</option>
            <option value="person">person</option>
            <option value="task">task</option>
          </select>
        </label>
        <div class="block text-sm">
          <span class="text-muted-foreground text-xs">Source</span>
          <SearchSelect
            :model-value="kpi.sourceRef"
            :options="sources.options(kpi.sourceKind)"
            :disabled="!kpi.sourceKind"
            :placeholder="sourcePlaceholder(kpi)"
            :empty-text="kpi.sourceKind === 'person' ? 'No people match.' : 'No tasks match.'"
            aria-label="Source"
            :data-testid="`kpi-source-${index}`"
            class="mt-1"
            @update:model-value="write(index, { sourceRef: $event })"
          />
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-4 text-sm">
        <label class="flex items-center gap-2">
          <input
            type="checkbox"
            :checked="kpi.onTargetWorseningRule"
            :data-testid="`kpi-amber-wrong-way-${index}`"
            @change="write(index, { onTargetWorseningRule: ($event.target as HTMLInputElement).checked })"
          />
          <span class="text-muted-foreground text-xs"
            >On target but moving the wrong way reads AMBER</span
          >
        </label>
        <label v-if="keyKpi" class="flex items-center gap-2">
          <input
            type="radio"
            :checked="kpi.isKey"
            :data-testid="`kpi-is-key-${index}`"
            @change="setKey(index)"
          />
          <span class="text-muted-foreground text-xs">Key KPI</span>
        </label>
        <button
          type="button"
          :data-testid="`kpi-remove-${index}`"
          class="text-muted-foreground ml-auto rounded-md border px-2 py-1 text-xs"
          @click="remove(index)"
        >
          Remove
        </button>
      </div>
    </div>
    <button
      type="button"
      data-testid="kpi-add"
      class="rounded-md border px-2 py-1 text-sm"
      @click="add"
    >
      Add a KPI
    </button>
  </div>
</template>
